import { ReportQueryFilters } from "./reports/reports";
import {
  CTBReport,
  CTBReportAPIResponse,
  ReportRejectReason,
  parseReport
} from "./reports/parseReports";
import { BaseFilters } from "../../../components/filters/FilterToolBar";
import { api, createURLSearchParams } from "../api";

type PaginatedReportData = {
  reports: CTBReport[];
  results: number;
};

export type ReportFilters = {
  severity?: string[];
  status?: string[];
  reportState?: string;
  type?: string;
  page?: number;
  includeArchived?: boolean | string;
  includeClosed?: boolean;
  program?: string;
  pentesterUsername?: string;
} & BaseFilters;

const reportsURL = "/v2/reports";

/**
 * The Reports API slice definition.
 *
 * Each endpoint is defined here, and is then available via the
 * generated hooks. Data fetched using these endpoints is cached
 * for any subsequent queries
 */
export const reportsApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Retrieve the list of paginated reports available to
     * the current user from the backend
     */
    getReports: builder.query<PaginatedReportData, ReportFilters>({
      query: filters => {
        const params = createURLSearchParams<ReportQueryFilters>({
          ...filters,
          severity: filters.severity ? filters.severity.join(",") : undefined,
          status: filters.status ? filters.status.join(",") : undefined,
          reportState: filters.reportState,
          page: filters.page ? filters.page.toString() : undefined,
          pageCount: filters.limit.toString(),
          includeArchived: filters.includeArchived,
          includeClosed: filters.includeClosed,
          program: filters.program,
          pentesterUsername: filters.pentesterUsername
        });

        return {
          url: reportsURL + "?" + params.toString(),
          method: "get"
        };
      },
      transformResponse: (response: {
        data: CTBReportAPIResponse[];
        results: number;
      }) => ({
        reports: response.data.map(report => parseReport(report)),
        results: response.results || 0
      }),
      providesTags: ["Reports"]
    }),
    /**
     * Retrieve a single report from the backend
     */
    getReport: builder.query<CTBReport, number>({
      query: id => ({ url: reportsURL + "/" + id, method: "get" }),
      transformResponse: (response: CTBReportAPIResponse[]) =>
        parseReport(response[0]),
      providesTags: (_, __, id) => [
        {
          type: "Report",
          id
        }
      ]
    }),
    /**
     * Makes a request to create/update a report on the backend.
     * Only works for researchers
     */
    updateReport: builder.mutation<CTBReport, { data: FormData }>({
      query: ({ data }) => ({
        url: reportsURL,
        method: "post",
        data
      }),
      transformResponse: (response: CTBReportAPIResponse) =>
        parseReport(response),
      invalidatesTags: (report?: CTBReport) => [
        {
          type: "Report",
          id: report?.id
        },
        "Reports"
      ]
    }),
    /**
     * Submits the given report
     */
    submitReport: builder.mutation<void, number>({
      query: id => ({ url: reportsURL + "/" + id + "/submit", method: "post" }),
      invalidatesTags: (_, __, id) => [
        {
          type: "Report",
          id
        },
        "Reports"
      ]
    }),

    deleteReport: builder.mutation<void, number>({
      query: id => ({
        url: `/v2/reports/${id}`,
        method: "DELETE"
      })
    }),
    /**
     * Updates the given reports status (as a business or admin)
     */
    updateReportState: builder.mutation<
      void,
      {
        id: number;
        state:
        | "approved"
        | "rejected"
        | "closed"
        | "request info"
        | "acceptRisk"
        | "nonActionableIssue"
        | "requestFix"
        | "requestRetest"
        | "undo"
        | "out of scope"
        // | "QA Disagrees"
        | "QA Modified";
        rejectReason?: ReportRejectReason;
      }
    >({
      query: ({ id, state, rejectReason }) => ({
        url: reportsURL + "/" + id + "/state",
        method: "post",
        data: {
          state,
          reason: rejectReason
        }
      }),
      invalidatesTags: (_, __, { id }) => [
        {
          type: "Report",
          id
        },
        "Reports"
      ]
    }),
    /**
     * Bulk update report states for business users
     */
    bulkUpdateReportStates: builder.mutation<
      {
        success: boolean;
        data: {
          updatedReports: any[];
          errors: any[];
          skippedReports: any[];
          totalUpdated: number;
          totalErrors: number;
          totalSkipped: number;
        };
        message: string;
      },
      {
        reportIds: number[];
        action: "requestFix" | "requestRetest";
        reason?: string;
      }
    >({
      query: ({ reportIds, action, reason }) => ({
        url: reportsURL + "/bulk-state-update",
        method: "post",
        data: {
          reportIds,
          action,
          reason
        }
      }),
      invalidatesTags: ["Reports"]
    }),
    /**
     * Bulk archive or unarchive reports
     */
    bulkArchiveReports: builder.mutation<
      { success: boolean; message: string; data: { updatedCount: number } },
      { reportIds: number[]; archive: boolean }
    >({
      query: ({ reportIds, archive }) => ({
        url: reportsURL + "/bulk-archive",
        method: "post",
        data: {
          reportIds,
          archive
        }
      }),
      invalidatesTags: ["Reports"]
    }),
    generateCertificate: builder.mutation<void, number>({
      query: id => ({
        url: reportsURL + "/certificate",
        method: "post",
        data: {
          id
        }
      }),
      invalidatesTags: (_, __, id) => [
        {
          type: "Report",
          id
        }
      ]
    }),
    getReportRecommendations: builder.query<
      { report_id: number; report_title: string; severity_category: string; submitted_date: string }[],
      number
    >({
      query: id => ({
        url: `/v2/reports/${id}/recommendations`,
        method: "get"
      }),
      transformResponse: (response: { recommendations: any[] }) =>
        response.recommendations || []
    }),
    /**
     * Mark a report as read by business users
     */
    markReportAsRead: builder.mutation<
      { message: string },
      number
    >({
      query: reportId => ({
        url: reportsURL + "/" + reportId + "/read",
        method: "put"
      }),
      invalidatesTags: (_, __, reportId) => [
        {
          type: "Report",
          id: reportId
        },
        "Reports"
      ]
    }),
    /**
     * Submit run scope snapshot for verification (Business)
     */
    submitRunScope: builder.mutation<{ message: string }, number>({
      query: runId => ({
        url: `/v2/programs/runs/${runId}/submit`,
        method: "PATCH"
      }),
      invalidatesTags: ["ProgramRuns"]
    }),
    /**
     * Verify and finalize run scope snapshot (Admin)
     */
    verifyRunScope: builder.mutation<{ message: string }, number>({
      query: runId => ({
        url: `/v2/programs/runs/${runId}/verify`,
        method: "PATCH"
      }),
      invalidatesTags: ["ProgramRuns"]
    }),
    /**
     * Refresh scope snapshot for a run
     */
    refreshRunScopeSnapshot: builder.mutation<{ message: string }, number>({
      query: runId => ({
        url: `/v2/programs/runs/${runId}/refresh-snapshot`,
        method: "POST"
      }),
      invalidatesTags: ["ProgramRuns"]
    })
  })
});

export const {
  useGetReportsQuery,
  useGetReportQuery,
  useUpdateReportMutation,
  useSubmitReportMutation,
  useUpdateReportStateMutation,
  useBulkUpdateReportStatesMutation,
  useDeleteReportMutation,
  useGenerateCertificateMutation,
  useGetReportRecommendationsQuery,
  useMarkReportAsReadMutation,
  useSubmitRunScopeMutation,
  useVerifyRunScopeMutation,
  useRefreshRunScopeSnapshotMutation,
  useBulkArchiveReportsMutation
} = reportsApi;
